// DeveloperPortal.jsx - Main container with routing for developer portal
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { DeveloperAuthProvider, useDeveloperAuth } from '../../contexts/DeveloperAuthContext';
import DeveloperAuth from './DeveloperAuth';
import DeveloperDashboard from './DeveloperDashboard';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, isConfigured } = useDeveloperAuth();

  if (!isConfigured) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-sm border border-gray-200">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Authentication Not Configured</h2>
          <p className="text-gray-600 text-sm">
            The developer portal requires Supabase authentication to be configured. Please contact the administrator.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-900 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/developers/login" replace />;
  }

  return children;
}

// Main portal content
function PortalContent() {
  return (
    <Routes>
      <Route path="login" element={<DeveloperAuth mode="login" />} />
      <Route path="signup" element={<DeveloperAuth mode="signup" />} />
      <Route path="forgot-password" element={<DeveloperAuth mode="forgot" />} />
      <Route
        path="dashboard/*"
        element={
          <ProtectedRoute>
            <DeveloperDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        index
        element={
          <ProtectedRoute>
            <Navigate to="/developers/dashboard" replace />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/developers" replace />} />
    </Routes>
  );
}

// Exported component with auth provider
function DeveloperPortal() {
  return (
    <DeveloperAuthProvider>
      <PortalContent />
    </DeveloperAuthProvider>
  );
}

export default DeveloperPortal;
